import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories, genres } from '@/mocks/comics';

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeGenre = searchParams.get('genre') || '全部';

  const currentCategory = categories.find((c) => c.key === type);
  const categoryLabel = currentCategory?.label || type || '';

  const filteredComics = comics.filter((comic) => {
    if (comic.category !== type) return false;
    if (activeGenre !== '全部' && !comic.genres.includes(activeGenre)) return false;
    return true;
  });

  const handleGenreChange = (genre: string) => {
    if (genre === '全部') {
      setSearchParams({});
    } else {
      setSearchParams({ genre });
    }
  };

  const availableGenres = ['全部', ...genres
    .filter((g) => comics.some((c) => c.category === type && c.genres.includes(g.key)))
    .map((g) => g.key)];

  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('rating');

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return b.views - a.views;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryLabel}推荐 - 香蕉漫画`,
    description: `香蕉漫画${categoryLabel}分类推荐，精选热门${categoryLabel}涵盖恋爱、奇幻、热血等所有风格，高清漫画免费在线阅读和人气排行榜。`,
    url: `https://bananamanga.com/category/${type}`,
    hasPart: sortedComics.map((comic, idx) => ({
      '@type': 'CreativeWork',
      name: comic.title,
      url: `https://bananamanga.com/comic/${comic.id}`,
      position: idx + 1,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <ScrollToTop />
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative h-64 md:h-80 flex items-center bg-secondary-500">
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-[1280px] mx-auto pt-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mt-3">
              {categoryLabel}推荐
            </h1>
            <p className="text-white/75 text-sm mt-2 max-w-lg">
              {currentCategory?.description || '精选热门漫画推荐'}
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-6 bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex flex-wrap gap-1.5 flex-1">
                {availableGenres.map((genre) => (
                  <button
                    key={genre}
                    onClick={() => handleGenreChange(genre)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeGenre === genre
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {(['rating', 'views', 'updated'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      sortBy === s
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s === 'rating' ? '评分' : s === 'views' ? '人气' : '最新'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <p className="text-sm text-foreground-500 mb-5">
              共找到 <strong className="text-foreground-800">{sortedComics.length}</strong> 部漫画
              {activeGenre !== '全部' && <span>（风格：{activeGenre}）</span>}
            </p>

            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无符合条件的漫画</p>
                <button
                  onClick={() => setSearchParams({})}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  清除筛选条件
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}